import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, ShoppingBag, Menu, X, Phone, 
  Instagram, Mail, MapPin, Settings, MessageCircle, ChevronRight, ChevronLeft, Sparkles, Star, TrendingUp, Loader2, ChevronDown, Grid3X3, User, LogIn, LogOut
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';
import { useCustomer } from '../context/CustomerContext';
import CartDrawer from './CartDrawer';
import ProductModal from './ProductModal';
import CustomerModal from './CustomerModal';
import CustomerAreaContent from './CustomerAreaContent';

const PRODUCTS_PER_PAGE = 20;

const CatalogLayout = ({ settings, categories, products, promoBanners = [], onOpenAdmin, showCustomerArea = false }) => {
  // Read category from URL on initial load
  const getInitialCategory = () => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('categoria') || params.get('category');
    if (categoryParam) {
      // Check if it's a valid category ID or slug
      const foundCategory = categories.find(c => c.id === categoryParam || c.name.toLowerCase().replace(/\s+/g, '-') === categoryParam.toLowerCase());
      if (foundCategory) return foundCategory.id;
      if (categoryParam === 'all' || categoryParam === 'todos') return 'all';
      if (categoryParam === 'highlights' || categoryParam === 'destaques') return 'highlights';
    }
    return 'highlights'; // Default to highlights
  };

  const [selectedCategory, setSelectedCategory] = useState('highlights');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [displayedCount, setDisplayedCount] = useState(PRODUCTS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const loadMoreRef = useRef(null);
  const categoriesDropdownMobileRef = useRef(null);
  const categoriesDropdownDesktopRef = useRef(null);
  const userDropdownRef = useRef(null);
  const { openCart, getTotalItems } = useCart();
  const { customer, isLoggedIn, logout } = useCustomer();
  const totalItems = getTotalItems();

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowUserDropdown(false);
    window.location.href = '/';
  };

  // Set category from URL when categories are loaded
  useEffect(() => {
    if (categories.length > 0) {
      const initialCategory = getInitialCategory();
      setSelectedCategory(initialCategory);
    }
  }, [categories]);

  // Update URL when category changes
  const handleCategoryChange = (categoryId) => {
    // If we're in customer area, redirect to main catalog with category
    if (showCustomerArea) {
      const categoryParam = categoryId === 'highlights' ? '' : `?categoria=${categoryId}`;
      window.location.href = '/' + categoryParam;
      return;
    }
    
    setSelectedCategory(categoryId);
    
    // Update URL without reloading
    const url = new URL(window.location);
    if (categoryId === 'highlights') {
      url.searchParams.delete('categoria');
    } else {
      url.searchParams.set('categoria', categoryId);
    }
    window.history.pushState({}, '', url);
  };

  // Categorias principais para mostrar na barra (marcadas com show_in_menu ou as primeiras 6)
  const menuCategories = categories.filter(c => c.show_in_menu).sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0));
  const mainCategories = menuCategories.length > 0 ? menuCategories : categories.slice(0, 6);

  // Auto-rotate banners
  useEffect(() => {
    if (promoBanners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex(prev => (prev + 1) % promoBanners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [promoBanners.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside either dropdown
      const mobileRef = categoriesDropdownMobileRef.current;
      const desktopRef = categoriesDropdownDesktopRef.current;
      
      const clickedInsideMobile = mobileRef && mobileRef.contains(event.target);
      const clickedInsideDesktop = desktopRef && desktopRef.contains(event.target);
      
      if (!clickedInsideMobile && !clickedInsideDesktop) {
        setShowAllCategories(false);
      }
    };
    
    // Use mousedown for desktop and touchend for mobile
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset displayed count when category changes
  useEffect(() => {
    setDisplayedCount(PRODUCTS_PER_PAGE);
  }, [selectedCategory, searchQuery]);

  // Produtos em destaque (featured) ou mais recentes
  const highlightedProducts = products.filter(p => p.featured || p.is_highlight).slice(0, 12);
  
  // Se não houver produtos destacados, pegar os 12 primeiros
  const featuredToShow = highlightedProducts.length > 0 ? highlightedProducts : products.slice(0, 12);

  const filteredProducts = selectedCategory === 'highlights' 
    ? featuredToShow
    : products.filter(p => {
        // Verifica category_ids (múltiplas categorias) OU category_id (compatibilidade)
        const matchesCategory = selectedCategory === 'all' || 
          p.category_id === selectedCategory || 
          (p.category_ids && p.category_ids.includes(selectedCategory));
        const matchesSearch = !searchQuery || 
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
      });

  // Produtos a serem exibidos (com limite para infinite scroll)
  const productsToDisplay = selectedCategory === 'all' 
    ? filteredProducts.slice(0, displayedCount)
    : filteredProducts;
  
  const hasMoreProducts = selectedCategory === 'all' && displayedCount < filteredProducts.length;

  // Infinite scroll observer
  const handleObserver = useCallback((entries) => {
    const [target] = entries;
    if (target.isIntersecting && hasMoreProducts && !isLoadingMore) {
      setIsLoadingMore(true);
      // Simular um pequeno delay para UX
      setTimeout(() => {
        setDisplayedCount(prev => prev + PRODUCTS_PER_PAGE);
        setIsLoadingMore(false);
      }, 300);
    }
  }, [hasMoreProducts, isLoadingMore]);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [handleObserver]);

  const openWhatsApp = () => {
    const whatsapp = settings?.whatsapp || '5555984551621';
    window.open(`https://wa.me/${whatsapp}?text=Olá! Gostaria de solicitar um orçamento.`, '_blank');
  };

  const formatPhone = (phone) => {
    if (!phone) return '(55) 98455-1621';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 13) {
      return `(${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }
    return phone;
  };

  const getLowestPrice = (product) => {
    if (!product.prices || product.prices.length === 0) return null;
    const prices = product.prices.map(p => p.price);
    return Math.min(...prices);
  };

  const scrollToProducts = () => {
    document.getElementById('produtos')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto">
          {/* Main Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 lg:px-8 h-14 sm:h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              <button 
                className="lg:hidden p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              </button>
              <a href="/" className="flex items-center">
                {settings?.logo_url ? (
                  <img 
                    src={settings.logo_url} 
                    alt={settings?.company_name || 'Gelatti'}
                    className="h-8 sm:h-10 lg:h-12 w-auto object-contain"
                  />
                ) : (
                  <span className="font-bold text-lg sm:text-xl text-[#195C75]">
                    {settings?.company_name || 'Gelatti'}
                  </span>
                )}
              </a>
            </div>

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#195C75]/20 focus:border-[#195C75] transition-all"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2 lg:gap-4">
              {/* Contact Info - Desktop */}
              <div className="hidden xl:flex items-center gap-2 text-sm text-slate-600 mr-4">
                <Phone className="w-4 h-4 text-[#195C75]" />
                <span className="font-medium">{formatPhone(settings?.whatsapp)}</span>
              </div>

              {/* Admin Button - Header (discreto) */}
              <button
                onClick={onOpenAdmin}
                className="hidden sm:flex p-2 sm:p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
                title="Painel Administrativo"
                data-testid="admin-header-btn"
              >
                <Settings className="w-5 h-5 sm:w-5 sm:h-5 text-slate-400 hover:text-[#195C75]" />
              </button>

              {/* My Account Button */}
              {isLoggedIn ? (
                <div className="relative" ref={userDropdownRef}>
                  <button
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="relative p-2 sm:p-2.5 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1.5"
                    title={`Olá, ${customer?.name?.split(' ')[0]}`}
                    data-testid="my-account-btn"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#195C75] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs sm:text-sm font-bold">
                        {customer?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden lg:block text-sm text-slate-700 font-medium max-w-[100px] truncate">
                      {customer?.name?.split(' ')[0]}
                    </span>
                    <ChevronDown className="hidden lg:block w-4 h-4 text-slate-400" />
                  </button>
                  
                  {/* User Dropdown */}
                  {showUserDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                      <a
                        href="/cliente"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        <User className="w-4 h-4" />
                        Minha Conta
                      </a>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sair
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowCustomerModal(true)}
                  className="relative p-2 sm:p-2.5 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1.5"
                  title="Minha Conta"
                  data-testid="my-account-btn"
                >
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                  <span className="hidden lg:block text-sm text-slate-600">Entrar</span>
                </button>
              )}

              {/* Cart Button */}
              <button
                onClick={openCart}
                className="relative p-2 sm:p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-[#195C75] text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>

              {/* WhatsApp Button */}
              <Button 
                onClick={openWhatsApp}
                className="hidden sm:flex bg-[#195C75] hover:bg-[#157A8C] text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg font-medium items-center gap-2 transition-all shadow-sm hover:shadow-md text-sm"
                data-testid="whatsapp-cta"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden lg:inline">Orçamento</span>
              </Button>
            </div>
          </div>

          {/* Mobile Search */}
          <div className="md:hidden px-3 sm:px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar produtos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-slate-50 border-slate-200 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Promotional Banners - Simple Image Only */}
      {!showCustomerArea && promoBanners.length > 0 && promoBanners[currentBannerIndex]?.image_url && (
        <section className="relative">
          {/* Banner Image - Full Width */}
          <div className="w-full">
            <img 
              src={promoBanners[currentBannerIndex].image_url}
              alt="Banner Promocional"
              className="w-full h-auto object-cover"
              style={{ maxHeight: '400px', objectFit: 'cover' }}
            />
          </div>

          {/* Navigation Dots - Only if multiple banners */}
          {promoBanners.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2">
              <button
                onClick={() => setCurrentBannerIndex(prev => (prev - 1 + promoBanners.length) % promoBanners.length)}
                className="p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {promoBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBannerIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentBannerIndex 
                      ? 'bg-white w-6' 
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                />
              ))}
              
              <button
                onClick={() => setCurrentBannerIndex(prev => (prev + 1) % promoBanners.length)}
                className="p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </section>
      )}

      {/* Fallback Banner when no promo banners */}
      {promoBanners.length === 0 && (
        <section className="relative bg-gradient-to-br from-[#0F5156] to-[#195C75] overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
          <div className="relative max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
            <div className="max-w-2xl">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                Brindes Personalizados
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-white/80 mb-4 sm:mb-6">
                Transforme suas ideias em produtos únicos
              </p>
              <Button 
                onClick={scrollToProducts}
                className="bg-white text-[#195C75] hover:bg-white/90 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base"
              >
                Ver Catálogo
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Horizontal Categories Bar */}
      <nav className="bg-[#195C75] shadow-md sticky top-14 sm:top-16 lg:top-20 z-40">
        <div className="max-w-[1800px] mx-auto">
          {/* Mobile: Simple 3 buttons */}
          <div className="lg:hidden">
            <div className="flex items-center justify-center">
              {/* Categories Dropdown - Mobile */}
              <div className="relative flex-1" ref={categoriesDropdownMobileRef}>
                <button
                  onClick={() => setShowAllCategories(!showAllCategories)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 font-medium text-sm transition-colors ${
                    showAllCategories || (selectedCategory !== 'highlights' && selectedCategory !== 'all')
                      ? 'bg-white text-[#195C75]'
                      : 'bg-white/90 text-[#195C75] hover:bg-white'
                  }`}
                  data-testid="all-categories-btn"
                >
                  <Menu className="w-4 h-4" />
                  <span>Categorias</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
                </button>

                {/* Categories Dropdown Panel - Mobile - Inside the relative container */}
                {showAllCategories && (
                  <>
                    {/* Backdrop to prevent clicks on elements behind */}
                    <div 
                      className="fixed inset-0 bg-black/20"
                      style={{ zIndex: 9998 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllCategories(false);
                      }}
                    />
                    <div 
                      className="fixed left-0 right-0 bg-white shadow-2xl border-t border-slate-200 max-h-[60vh] overflow-y-auto"
                      style={{ top: '120px', zIndex: 9999 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-4">
                        {/* All Products Option */}
                        <button
                          onClick={(e) => { 
                            e.stopPropagation();
                            handleCategoryChange('all'); 
                            setShowAllCategories(false); 
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                            selectedCategory === 'all' 
                              ? 'bg-[#195C75] text-white' 
                              : 'hover:bg-slate-100 text-slate-700'
                          }`}
                        >
                          <Grid3X3 className="w-5 h-5" />
                          <span className="font-medium">Todos os Produtos</span>
                          <span className="ml-auto text-sm opacity-70">({products.length})</span>
                        </button>

                        {/* Divider */}
                        <div className="my-3 border-t border-slate-200"></div>

                        {/* Categories List */}
                        <div className="space-y-1">
                          {categories.map((cat) => {
                            const count = products.filter(p => p.category_id === cat.id || (p.category_ids && p.category_ids.includes(cat.id))).length;
                            return (
                              <button
                                key={cat.id}
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  handleCategoryChange(cat.id); 
                                  setShowAllCategories(false); 
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                                  selectedCategory === cat.id 
                                    ? 'bg-[#195C75] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                              >
                                <span className="font-medium">{cat.name}</span>
                                {count > 0 && <span className="ml-auto text-sm opacity-70">({count})</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Destaques - Mobile */}
              <button
                onClick={() => handleCategoryChange('highlights')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  selectedCategory === 'highlights' 
                    ? 'bg-white text-[#195C75]' 
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                <Star className={`w-4 h-4 ${selectedCategory === 'highlights' ? 'fill-[#195C75]' : ''}`} />
                <span>Destaques</span>
              </button>

              {/* Cevador - Mobile */}
              <a
                href="/cevador"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                <span>Cevador</span>
              </a>
            </div>
          </div>

          {/* Desktop: Original layout */}
          <div className="hidden lg:flex items-center justify-center">
            {/* All Categories Dropdown */}
            <div className="relative" ref={categoriesDropdownDesktopRef}>
              <button
                onClick={() => setShowAllCategories(!showAllCategories)}
                className="flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-white hover:bg-slate-50 text-[#195C75] font-medium transition-colors"
                data-testid="all-categories-btn-desktop"
              >
                <Menu className="w-5 h-5" />
                <span>TODAS CATEGORIAS</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
              </button>

              {/* Categories Dropdown Panel - Desktop */}
              {showAllCategories && (
                <div className="absolute top-full left-0 w-[600px] lg:w-[800px] bg-white shadow-2xl border border-slate-200 z-50 max-h-[70vh] overflow-y-auto">
                  <div className="p-4 sm:p-6">
                    {/* Special Options */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4 pb-4 border-b border-slate-200">
                      <button
                        onClick={() => { handleCategoryChange('highlights'); setShowAllCategories(false); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                          selectedCategory === 'highlights' 
                            ? 'bg-[#195C75]/10 text-[#195C75] font-semibold' 
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <Star className="w-4 h-4 text-[#195C75]" />
                        Destaques
                      </button>
                      <button
                        onClick={() => { handleCategoryChange('all'); setShowAllCategories(false); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                          selectedCategory === 'all' 
                            ? 'bg-[#195C75]/10 text-[#195C75] font-semibold' 
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <Grid3X3 className="w-4 h-4" />
                        Todos ({products.length})
                      </button>
                    </div>

                    {/* Categories Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
                      {categories.map((cat) => {
                        const count = products.filter(p => p.category_id === cat.id || (p.category_ids && p.category_ids.includes(cat.id))).length;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => { handleCategoryChange(cat.id); setShowAllCategories(false); }}
                            className={`px-3 py-2 rounded-lg text-left text-sm transition-all ${
                              selectedCategory === cat.id 
                                ? 'bg-[#195C75]/10 text-[#195C75] font-semibold' 
                                : 'hover:bg-slate-50 text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            {cat.name}
                            {count > 0 && <span className="text-slate-400 ml-1">({count})</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Category Links - Desktop */}
            <div className="flex items-center">
              {mainCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-4 py-3 sm:py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === cat.id 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`quick-cat-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Destaques Button - Desktop */}
            <button
              onClick={() => handleCategoryChange('highlights')}
              className={`px-4 py-3 sm:py-4 text-sm font-medium flex items-center gap-2 transition-colors ${
                selectedCategory === 'highlights' 
                  ? 'bg-white text-[#195C75]' 
                  : 'bg-white/90 text-[#195C75] hover:bg-white'
              }`}
              data-testid="highlights-btn"
            >
              <Star className={`w-4 h-4 ${selectedCategory === 'highlights' ? 'fill-[#195C75] text-[#195C75]' : ''}`} />
              <span>Destaques</span>
            </button>

            {/* Cevadores Button - Desktop */}
            <a
              href="/cevador"
              className="px-4 py-3 sm:py-4 text-sm font-medium flex items-center gap-2 transition-colors bg-emerald-500 text-white hover:bg-emerald-600"
              data-testid="cevadores-btn"
            >
              <Sparkles className="w-4 h-4" />
              <span>Cevadores</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content - Full Width now */}
      <div className="flex-1" id="produtos">
        {showCustomerArea ? (
          <CustomerAreaContent onOpenLoginModal={() => setShowCustomerModal(true)} />
        ) : (
        <main className="max-w-[1800px] mx-auto p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                  {selectedCategory === 'highlights' && <Star className="w-6 h-6 text-[#195C75]" />}
                  {selectedCategory === 'highlights' 
                    ? 'Destaques' 
                    : selectedCategory === 'all' 
                      ? 'Todos os Produtos' 
                      : categories.find(c => c.id === selectedCategory)?.name || 'Produtos'}
                </h1>
                <p className="text-slate-500 mt-1 text-sm sm:text-base">
                  {selectedCategory === 'highlights' 
                    ? 'Nossos produtos em destaque'
                    : selectedCategory === 'all' && hasMoreProducts
                      ? `Mostrando ${productsToDisplay.length} de ${filteredProducts.length} produtos`
                      : `${filteredProducts.length} ${filteredProducts.length === 1 ? 'produto encontrado' : 'produtos encontrados'}`}
                </p>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {productsToDisplay.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                {productsToDisplay.map((product) => (
                  <ProductCard 
                    key={product.id} 
                    product={product}
                    lowestPrice={getLowestPrice(product)}
                    onClick={() => setSelectedProduct(product)}
                  />
                ))}
              </div>

              {/* Infinite Scroll Trigger / Load More */}
              {selectedCategory === 'all' && (
                <div ref={loadMoreRef} className="py-8 flex justify-center">
                  {isLoadingMore && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Carregando mais produtos...</span>
                    </div>
                  )}
                  {!hasMoreProducts && filteredProducts.length > PRODUCTS_PER_PAGE && (
                    <p className="text-slate-400 text-sm">
                      Todos os {filteredProducts.length} produtos foram carregados
                    </p>
                  )}
                </div>
              )}

              {/* CTA para ver todos quando está em Destaques */}
              {selectedCategory === 'highlights' && products.length > featuredToShow.length && (
                <div className="mt-8 text-center">
                  <Button
                    onClick={() => handleCategoryChange('all')}
                    variant="outline"
                    className="border-[#195C75] text-[#195C75] hover:bg-[#195C75] hover:text-white px-8 py-3"
                    data-testid="view-all-products-btn"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Ver todos os {products.length} produtos
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 sm:py-16 bg-white rounded-xl border border-slate-200">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
              </div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-700 mb-2">
                Nenhum produto encontrado
              </h3>
              <p className="text-slate-500 text-sm sm:text-base">
                Tente ajustar sua busca ou selecione outra categoria
              </p>
            </div>
          )}
        </main>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 mt-auto">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Brand */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-white font-bold text-xl mb-4">
                {settings?.company_name || 'Gelatti'}
              </h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                {settings?.tagline || 'Soluções em impressões personalizadas com qualidade e criatividade.'}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-3 sm:mb-4 text-sm sm:text-base">Navegação</h4>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <button 
                    onClick={() => handleCategoryChange('all')}
                    className="text-slate-400 hover:text-[#195C75] text-xs sm:text-sm transition-colors"
                  >
                    Todos os Produtos
                  </button>
                </li>
                {categories.slice(0, 4).map(cat => (
                  <li key={cat.id}>
                    <button 
                      onClick={() => handleCategoryChange(cat.id)}
                      className="text-slate-400 hover:text-[#195C75] text-xs sm:text-sm transition-colors"
                    >
                      {cat.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-white mb-3 sm:mb-4 text-sm sm:text-base">Contato</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-center gap-2 sm:gap-3 text-slate-400 text-xs sm:text-sm">
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" />
                  <span className="truncate">{formatPhone(settings?.whatsapp)}</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-slate-400 text-xs sm:text-sm">
                  <Instagram className="w-3 h-3 sm:w-4 sm:h-4 text-pink-400 flex-shrink-0" />
                  <span className="truncate">{settings?.instagram || '@gelattisolucoes'}</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-slate-400 text-xs sm:text-sm">
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 flex-shrink-0" />
                  <span className="truncate">{settings?.email || 'contato@gelatti.com.br'}</span>
                </li>
              </ul>
            </div>

            {/* Location */}
            <div className="col-span-2 sm:col-span-1">
              <h4 className="font-heading font-semibold text-white mb-3 sm:mb-4 text-sm sm:text-base">Localização</h4>
              <div className="flex items-start gap-2 sm:gap-3 text-slate-400 text-xs sm:text-sm">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-[#195C75] mt-0.5 flex-shrink-0" />
                <div>
                  <p>{settings?.address || 'Rua Carlos Hisserich 42, Sala 102'}</p>
                  <p>Centro, Panambi/RS</p>
                  <p>CEP: {settings?.cep || '98280-000'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 mt-6 sm:mt-10 pt-4 sm:pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-slate-500 text-xs sm:text-sm text-center sm:text-left">
              © {new Date().getFullYear()} {settings?.company_name || 'Gelatti'}. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        categories={categories}
        products={products}
        selectedCategory={selectedCategory}
        onSelectCategory={(id) => {
          handleCategoryChange(id);
          setMobileMenuOpen(false);
        }}
        settings={settings}
        onOpenAdmin={onOpenAdmin}
        featuredCount={featuredToShow.length}
      />

      {/* Cart Drawer */}
      <CartDrawer settings={settings} onOpenCustomerModal={() => setShowCustomerModal(true)} />

      {/* Customer Modal */}
      <CustomerModal 
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
      />

      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        settings={settings}
      />

      {/* WhatsApp Float - Mobile */}
      <button
        onClick={openWhatsApp}
        className="sm:hidden fixed bottom-4 right-4 w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg shadow-green-500/30 flex items-center justify-center z-40 transition-all hover:scale-110"
        data-testid="whatsapp-float"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, lowestPrice, onClick }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  return (
    <div 
      className="group relative bg-white rounded-lg sm:rounded-xl border border-slate-200 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-[#195C75]/30 hover:-translate-y-1 w-full"
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
    >
      {/* Product Image - Square with object-cover */}
      <div className="aspect-square bg-slate-50 overflow-hidden">
        <img 
          src={product.image_url || 'https://via.placeholder.com/400x400?text=Produto'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      <div className="p-2 sm:p-3 lg:p-4">
        {/* Product Code */}
        <p className="font-mono text-[8px] sm:text-[10px] text-slate-400 uppercase tracking-wider mb-0.5 sm:mb-1">
          {product.code || `#${product.id?.slice(0, 6)}`}
        </p>

        {/* Product Name */}
        <h3 className="font-semibold text-slate-800 text-xs sm:text-sm leading-tight line-clamp-2 mb-2 sm:mb-3 group-hover:text-[#195C75] transition-colors min-h-[2rem] sm:min-h-[2.5rem]">
          {product.name}
        </h3>
      </div>

      {/* Min Quantity Badge */}
      {product.min_quantity > 1 && (
        <div className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 bg-slate-800/90 text-white text-[8px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded sm:rounded-md font-medium backdrop-blur-sm">
          Mín. {product.min_quantity}
        </div>
      )}
    </div>
  );
};

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose, categories, products, selectedCategory, onSelectCategory, settings, onOpenAdmin, featuredCount }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Menu Panel */}
      <div className="absolute top-0 left-0 bottom-0 w-72 sm:w-80 max-w-[85vw] bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-slate-100">
          {settings?.logo_url ? (
            <img 
              src={settings.logo_url} 
              alt="Logo"
              className="h-7 sm:h-8 w-auto object-contain"
            />
          ) : (
            <span className="font-bold text-lg text-[#195C75]">
              {settings?.company_name || 'Gelatti'}
            </span>
          )}
          <button 
            onClick={onClose}
            className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Categories */}
        <nav className="p-3 sm:p-4">
          <h2 className="font-heading text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
            Categorias
          </h2>
          <ul className="space-y-1">
            {/* Destaques */}
            <li>
              <button
                onClick={() => onSelectCategory('highlights')}
                className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-left transition-all text-sm ${
                  selectedCategory === 'highlights'
                    ? 'bg-[#195C75] text-white'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Destaques
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedCategory === 'highlights' ? 'bg-white/20' : 'bg-[#195C75]/10 text-[#195C75]'
                }`}>
                  {featuredCount}
                </span>
              </button>
            </li>
            {/* Todos os Produtos */}
            <li>
              <button
                onClick={() => onSelectCategory('all')}
                className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-left transition-all text-sm ${
                  selectedCategory === 'all'
                    ? 'bg-[#195C75] text-white'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>Todos os Produtos</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedCategory === 'all' ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {products.length}
                </span>
              </button>
            </li>
            {categories.map((cat) => {
              // Conta produtos que pertencem à categoria (category_ids OU category_id)
              const count = products.filter(p => p.category_id === cat.id || (p.category_ids && p.category_ids.includes(cat.id))).length;
              return (
                <li key={cat.id}>
                  <button
                    onClick={() => onSelectCategory(cat.id)}
                    className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-left transition-all text-sm ${
                      selectedCategory === cat.id
                        ? 'bg-[#195C75] text-white'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedCategory === cat.id ? 'bg-white/20' : 'bg-slate-100'
                    }`}>
                      {count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-slate-100 mt-auto">
          <button
            onClick={() => {
              onClose();
              onOpenAdmin();
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 text-slate-500 hover:text-[#195C75] hover:bg-slate-50 rounded-lg transition-colors text-sm"
          >
            <Settings className="w-4 h-4" />
            <span>Área Administrativa</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CatalogLayout;
